/**
 * Which statement the page is showing, as it lives in the URL.
 *
 * IN THE URL, not in component state, and on this screen it matters more than on most. A
 * statement is a document somebody produces for a specific owner and month, then sends to a
 * colleague — "check OW000003's August before I send it" — reloads after fixing a receipt, and
 * comes back to when the owner rings. Component state survives none of those. It also makes the
 * query key: changing the month starts a fresh read rather than showing August's figures under a
 * July heading while the new one loads.
 *
 * ITS OWN MODULE, AND THE SPLIT IS A BUNDLE DECISION — the same one `auditSearch.ts` records.
 * `validateSearch` runs during ROUTING, so whatever this file imports lands in the eager bundle
 * on every page of the app. It therefore imports NOTHING. Anything richer belongs in
 * `statement.ts`, which is only reached from the report's own chunk.
 */

export interface StatementSelection {
  /** The owner's ref sequence — `OW000003` is `3`. Absent until somebody picks one. */
  owner?: number;
  /** The month, as its first day. Absent means "the one we are in", resolved by the screen. */
  period?: string;
}

const PERIOD = /^\d{4}-\d{2}-01$/;

/**
 * Read the selection out of the URL.
 *
 * SHAPE-CHECKED ONLY, like the audit report's. A month that does not exist is refused by the
 * contract at the API boundary — which is the boundary a pasted URL crosses anyway — and the
 * server's sentence beside the picker beats a router error page.
 *
 * A period that is not the first of a month is DROPPED rather than rounded down. `?period=
 * 2026-08-14` is a URL somebody edited by hand, and quietly reinterpreting it as August would
 * teach them the page accepts dates when what it accepts is months.
 */
export function parseStatementSearch(search: Record<string, unknown>): StatementSelection {
  const owner = Number(search.owner);

  return {
    ...(Number.isSafeInteger(owner) && owner > 0 ? { owner } : {}),
    ...(typeof search.period === 'string' && PERIOD.test(search.period)
      ? { period: search.period }
      : {}),
  };
}
